import { useMemo } from "react";
import { MemberBadges } from "@/app/components/MemberBadges";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/app/components/ui/tooltip";
import { useAvatar } from "@/app/hooks/useAvatars";
import type { TeamMember } from "@/app/hooks/useDagStore";

interface AvatarProps {
  member: TeamMember;
  size?: "sm" | "md" | "lg";
  showRoleOverlay?: boolean;
  className?: string;
}

/**
 * Gets the appropriate emoji for a team member's role
 * TODO(sno): generate this dynamicly
 */
function getRoleEmoji(role: string | null): string {
  if (!role) return "👤";
  const lowerRole = role.toLowerCase();
  if (lowerRole.includes("core team")) return "🚀";
  if (lowerRole.includes("part time contractor")) return "⏳";
  if (lowerRole.includes("full time contractor")) return "💻";
  if (lowerRole.includes("honorary")) return "🏅";
  if (lowerRole.includes("archived")) return "📦";
  if (lowerRole.includes("collaborator")) return "🤝";
  return "👤";
}

/**
 * Gets size classes for different avatar sizes
 */
function getSizeClasses(size: "sm" | "md" | "lg") {
  switch (size) {
    case "sm":
      return {
        avatar: "w-8 h-8",
        emoji: "w-3 h-3 text-xs",
        emojiContainer: "w-4 h-4",
      };
    case "md":
      return {
        avatar: "w-10 h-10",
        emoji: "w-3.5 h-3.5 text-sm",
        emojiContainer: "w-5 h-5",
      };
    case "lg":
      return {
        avatar: "w-12 h-12",
        emoji: "w-4 h-4 text-base",
        emojiContainer: "w-6 h-6",
      };
  }
}

export function Avatar({
  member,
  size = "md",
  showRoleOverlay = true,
  className = "",
}: AvatarProps) {
  // Get avatar URL using the common hook
  const avatarData = useAvatar(member.notionUserIds);

  // Fallback to checking member's notionUsers if avatarData not loaded yet
  const finalAvatarUrl = useMemo(() => {
    return avatarData ?? member.notionUsers?.find((user) => user.avatar)?.avatar;
  }, [avatarData, member.notionUsers]);

  const roleEmoji = getRoleEmoji(member.role);
  const sizeClasses = getSizeClasses(size);

  return (
    <div className={`relative inline-block ${className}`}>
      {finalAvatarUrl ? (
        <img
          src={finalAvatarUrl}
          alt={`${member.name} avatar`}
          className={`${sizeClasses.avatar} rounded-full object-cover`}
        />
      ) : (
        <div
          className={`${sizeClasses.avatar} rounded-full bg-gray-300 flex items-center justify-center`}
        >
          <span className="text-gray-600 font-medium">
            {member.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)}
          </span>
        </div>
      )}

      {showRoleOverlay && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={`absolute -bottom-0.5 -right-0.5 ${sizeClasses.emojiContainer} bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-200 cursor-help`}
            >
              <span className={sizeClasses.emoji}>{roleEmoji}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent className="bg-white text-gray-900 border border-gray-200 shadow-lg p-3 max-w-xs">
            <MemberBadges role={member.role} team={member.team} />
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
